import React, { useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Modal } from '../ui/Modal';
import { api, getPropertyDocumentDownloadUrl } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import type { AccountStatement, Investment } from '../../types';

type StatementRow = AccountStatement & {
  property?: { id: string; name: string };
};

function num(v: unknown): number {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  return parseFloat(String(v)) || 0;
}

function mapStatement(raw: any): StatementRow {
  const propertyName = raw.property?.name ?? raw.propertyName ?? '';
  return {
    id: raw.id,
    investorId: raw.investorId,
    propertyId: raw.propertyId,
    propertyName,
    month: raw.month,
    year: raw.year,
    period: raw.period,
    income: num(raw.income),
    expenses: num(raw.expenses),
    netIncome: num(raw.netIncome),
    ownershipPct: num(raw.ownershipPct),
    investorShare: num(raw.investorShare),
    fileUrl: raw.fileUrl,
    createdAt: raw.createdAt,
    units: raw.units,
    initialInvestment: raw.initialInvestment != null ? num(raw.initialInvestment) : undefined,
    totalAccumulatedInvestment:
      raw.totalAccumulatedInvestment != null ? num(raw.totalAccumulatedInvestment) : undefined,
    currentCapital: raw.currentCapital != null ? num(raw.currentCapital) : undefined,
    currentPropertyValue: raw.currentPropertyValue != null ? num(raw.currentPropertyValue) : undefined,
    monthlyGrossIncome: raw.monthlyGrossIncome != null ? num(raw.monthlyGrossIncome) : undefined,
    monthlyNetIncome: raw.monthlyNetIncome != null ? num(raw.monthlyNetIncome) : undefined,
    accumulatedYearlyIncome: raw.accumulatedYearlyIncome != null ? num(raw.accumulatedYearlyIncome) : undefined,
    monthlyROI: raw.monthlyROI != null ? num(raw.monthlyROI) : undefined,
    annualROI: raw.annualROI != null ? num(raw.annualROI) : undefined,
    annualYield: raw.annualYield != null ? num(raw.annualYield) : undefined,
    initialAssetValue: raw.initialAssetValue != null ? num(raw.initialAssetValue) : undefined,
    currentEstimatedValue: raw.currentEstimatedValue != null ? num(raw.currentEstimatedValue) : undefined,
    annualAppreciation: raw.annualAppreciation != null ? num(raw.annualAppreciation) : undefined,
    totalNetFlow: raw.totalNetFlow != null ? num(raw.totalNetFlow) : undefined,
    totalGain: raw.totalGain != null ? num(raw.totalGain) : undefined,
    totalReturnOnInvestment:
      raw.totalReturnOnInvestment != null ? num(raw.totalReturnOnInvestment) : undefined,
    property: raw.property,
  };
}

type Props = {
  investorId: string;
  activeInvestments: Investment[];
};

export function InvestorAccountStatementsTab({ investorId, activeInvestments }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canViewPrices = hasPermission('view_prices');
  const canCreate = hasPermission('manage_investors') || hasPermission('update_investors');
  const canDelete = hasPermission('manage_investors');

  const [modalOpen, setModalOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [annualDetailOpen, setAnnualDetailOpen] = useState(false);
  const [selectedStatement, setSelectedStatement] = useState<StatementRow | null>(null);
  const [selectedAnnual, setSelectedAnnual] = useState<{
    propertyId: string;
    propertyName: string;
    year: number;
    data: {
      year: number;
      totalIncome: number;
      totalExpenses: number;
      totalNetIncome: number;
      totalProfit: number;
      months: StatementRow[];
    } | null;
  } | null>(null);
  const [form, setForm] = useState({
    propertyId: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    income: '',
    expenses: '',
    netIncome: '',
    investorShare: '',
  });
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const resetForm = () => {
    setForm({
      propertyId: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      income: '',
      expenses: '',
      netIncome: '',
      investorShare: '',
    });
    setPdfFile(null);
    setFormError(null);
  };

  const handleCloseModal = () => {
    resetForm();
    setModalOpen(false);
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['investor-statements', investorId],
    queryFn: () => api.getInvestorStatements(investorId),
    enabled: !!investorId,
    staleTime: 30_000,
  });

  const statements = useMemo(() => (Array.isArray(data) ? data : []).map(mapStatement), [data]);

  const propertyGroups = useMemo(() => {
    const acc: Record<string, { propertyId: string; propertyName: string; statements: StatementRow[] }> = {};
    for (const s of statements) {
      const name = s.propertyName || s.property?.name || 'Propiedad';
      if (!acc[s.propertyId]) {
        acc[s.propertyId] = { propertyId: s.propertyId, propertyName: name, statements: [] };
      }
      acc[s.propertyId].statements.push(s);
    }
    Object.values(acc).forEach((g) => {
      g.statements.sort((a, b) => (a.year !== b.year ? b.year - a.year : b.month - a.month));
    });
    return Object.values(acc);
  }, [statements]);
  const currentYear = new Date().getFullYear();

  const annualQueries = useQueries({
    queries: propertyGroups.map((g) => ({
      queryKey: ['account-statements-annual', investorId, g.propertyId, currentYear],
      queryFn: () => api.getAnnualAccountStatement(investorId, g.propertyId, currentYear),
      enabled: !!investorId && !!g.propertyId,
      staleTime: 30_000,
    })),
  });

  const annualByPropertyId = useMemo(() => {
    const map = new Map<string, any>();
    propertyGroups.forEach((g, idx) => {
      map.set(g.propertyId, annualQueries[idx]?.data ?? null);
    });
    return map;
  }, [propertyGroups, annualQueries]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const income = parseFloat(form.income.replace(',', '.')) || 0;
      const expenses = parseFloat(form.expenses.replace(',', '.')) || 0;
      const netIncome = parseFloat(form.netIncome.replace(',', '.')) || 0;
      const investorShare = parseFloat(form.investorShare.replace(',', '.')) || 0;
      if (!form.propertyId.trim()) throw new Error('Selecciona una propiedad');
      let fileUrl: string | undefined;
      if (pdfFile) {
        const res = await api.uploadAccountStatementFile(pdfFile);
        fileUrl = res.fileUrls?.[0];
      }
      return api.createAccountStatement({
        investorId,
        propertyId: form.propertyId,
        month: form.month,
        year: form.year,
        income,
        expenses,
        netIncome,
        investorShare,
        fileUrl,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['investor-statements', investorId] });
      await queryClient.invalidateQueries({ queryKey: ['investor-roi', investorId] });
      await queryClient.invalidateQueries({ queryKey: ['investor', investorId] });
      handleCloseModal();
    },
    onError: (e: Error) => setFormError(e.message || 'Error al guardar'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteAccountStatement(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['investor-statements', investorId] });
      await queryClient.invalidateQueries({ queryKey: ['investor-roi', investorId] });
      await queryClient.invalidateQueries({ queryKey: ['investor', investorId] });
    },
  });

  const formatCurrency = (value: number) =>
    canViewPrices ? `$${num(value).toLocaleString('es-MX')}` : '—';
  const formatPct = (value: number) => `${num(value).toFixed(2)}%`;

  const formatMonthYear = (month: number, year: number) => {
    const d = new Date(year, month - 1, 1);
    return d.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }).replace(/^\w/, (c) => c.toUpperCase());
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">
        {error instanceof Error ? error.message : 'Error al cargar estados de cuenta'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Estados de cuenta</h2>
          <p className="text-sm text-gray-500 mt-1">Registros por propiedad y mes (distribuciones / participaciones).</p>
        </div>
        {canCreate && (
          <Button onClick={() => setModalOpen(true)}>
            + Registrar estado de cuenta
          </Button>
        )}
      </div>

      {propertyGroups.length === 0 ? (
        <Card>
          <div className="text-center py-12 text-gray-500">No hay estados de cuenta registrados.</div>
        </Card>
      ) : (
        <div className="space-y-6">
          {propertyGroups.map((g) => {
            const latest = g.statements[0] ?? null;
            const annual = annualByPropertyId.get(g.propertyId) as
              | {
                  year: number;
                  totalIncome: number;
                  totalExpenses: number;
                  totalNetIncome: number;
                  totalProfit: number;
                  months: StatementRow[];
                }
              | null;
            const annualLoading = annualQueries[propertyGroups.findIndex((x) => x.propertyId === g.propertyId)]?.isLoading;

            return (
              <Card
                key={`property-card-${g.propertyId}`}
                title={g.propertyName}
                action={
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      navigate(`/inversionistas/${investorId}/estado-de-cuenta/${g.propertyId}`)
                    }
                  >
                    Ver más
                  </Button>
                }
              >
                <div className="flex flex-col gap-3 w-full">
                  <div className="w-full rounded-lg border border-gray-200 p-3">
                    <h4 className="text-sm font-semibold text-gray-900">Último mes registrado</h4>
                    {latest ? (
                      <div className="space-y-2 mt-2">
                        <p className="text-sm text-gray-600">
                          Periodo:{' '}
                          <span className="font-medium text-gray-900">
                            {formatMonthYear(latest.month, latest.year)}
                          </span>
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-xs text-gray-500">Ingreso</p>
                            <p className="font-semibold text-green-600">
                              {formatCurrency(latest.income)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Egreso</p>
                            <p className="font-semibold text-red-500">
                              {formatCurrency(latest.expenses)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Ingreso neto</p>
                            <p className="font-semibold text-gray-900">
                              {formatCurrency(latest.netIncome)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Utilidad</p>
                            <p className="font-semibold text-primary">
                              {formatCurrency(latest.investorShare)}
                            </p>
                          </div>
                        </div>
                        {latest.fileUrl && (
                          <div className="pt-1">
                            <a
                              href={getPropertyDocumentDownloadUrl(latest.fileUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                            >
                              <Download className="w-4 h-4" />
                              Descargar reporte
                            </a>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 mt-2">Sin registros.</p>
                    )}
                  </div>

                  <div className="w-full rounded-lg border border-gray-200 p-3">
                    <h4 className="text-sm font-semibold text-gray-900">
                      Estado de cuenta anual {currentYear}
                    </h4>
                    <div className="mt-2">
                      {annualLoading ? (
                        <p className="text-sm text-gray-500">Cargando resumen anual...</p>
                      ) : annual ? (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <p className="text-xs text-gray-500">Ingreso total</p>
                              <p className="font-semibold text-green-600">
                                {formatCurrency(annual.totalIncome)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Egreso total</p>
                              <p className="font-semibold text-red-500">
                                {formatCurrency(annual.totalExpenses)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Ingreso neto total</p>
                              <p className="font-semibold text-gray-900">
                                {formatCurrency(annual.totalNetIncome)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Utilidad total</p>
                              <p className="font-semibold text-primary">
                                {formatCurrency(annual.totalProfit)}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedAnnual({
                                propertyId: g.propertyId,
                                propertyName: g.propertyName,
                                year: currentYear,
                                data: annual,
                              });
                              setAnnualDetailOpen(true);
                            }}
                            className="inline-flex text-xs font-medium text-primary hover:underline"
                          >
                            Ver desglose mensual
                          </button>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Sin datos para el año actual.</p>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title="Registrar estado de cuenta / distribución"
        size="lg"
      >
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            setFormError(null);
            createMutation.mutate();
          }}
        >
          {formError && <div className="text-sm text-red-600">{formError}</div>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Propiedad *</label>
            <select
              required
              value={form.propertyId}
              onChange={(e) => setForm((f) => ({ ...f, propertyId: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            >
              <option value="">Selecciona…</option>
              {activeInvestments.map((inv) => (
                <option key={inv.propertyId} value={inv.propertyId}>
                  {inv.propertyName || inv.propertyId} ({Number(inv.ownershipPct)}%)
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mes</label>
              <select
                value={form.month}
                onChange={(e) => setForm((f) => ({ ...f, month: Number(e.target.value) }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Año</label>
              <input
                type="number"
                min={2020}
                max={2100}
                value={form.year}
                onChange={(e) => setForm((f) => ({ ...f, year: Number(e.target.value) }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ingreso (MXN)</label>
            <input
              type="text"
              inputMode="decimal"
              value={form.income}
              onChange={(e) => setForm((f) => ({ ...f, income: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Egreso (MXN)</label>
            <input
              type="text"
              inputMode="decimal"
              value={form.expenses}
              onChange={(e) => setForm((f) => ({ ...f, expenses: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ingreso Neto (MXN)</label>
            <input
              type="text"
              inputMode="decimal"
              value={form.netIncome}
              onChange={(e) => setForm((f) => ({ ...f, netIncome: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Utilidad (MXN)</label>
            <input
              type="text"
              inputMode="decimal"
              value={form.investorShare}
              onChange={(e) => setForm((f) => ({ ...f, investorShare: e.target.value }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PDF del estado de cuenta (opcional)
            </label>
            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setPdfFile(file);
              }}
              className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 file:mr-3 file:rounded file:border-0 file:bg-gray-100 file:px-3 file:py-1 file:text-sm"
            />
            {pdfFile && (
              <p className="text-xs text-gray-500 mt-1">{pdfFile.name}</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelectedStatement(null);
        }}
        title="Estado de Cuenta por Inversionista"
        size="xl"
      >
        {selectedStatement && (
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-900">Información General</div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Propiedad / Proyecto</p>
                  <p className="font-medium text-gray-900">{selectedStatement.propertyName || 'Propiedad'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Unidad(es)</p>
                  <p className="font-medium text-gray-900">{selectedStatement.units || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Periodo del reporte</p>
                  <p className="font-medium text-gray-900">{formatMonthYear(selectedStatement.month, selectedStatement.year)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Participación del inversionista (%)</p>
                  <p className="font-medium text-gray-900">{num(selectedStatement.ownershipPct).toFixed(0)}%</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-900">Inversión</div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Inversión inicial</p>
                  <p className="font-semibold text-gray-900">{formatCurrency(num(selectedStatement.initialInvestment))}</p>
                </div>
                <div>
                  <p className="text-gray-500">Inversión total acumulada</p>
                  <p className="font-semibold text-gray-900">{formatCurrency(num(selectedStatement.totalAccumulatedInvestment))}</p>
                </div>
                <div>
                  <p className="text-gray-500">Capital actual estimado</p>
                  <p className="font-semibold text-gray-900">{formatCurrency(num(selectedStatement.currentCapital))}</p>
                </div>
                <div>
                  <p className="text-gray-500">Valor actual de la propiedad</p>
                  <p className="font-semibold text-gray-900">{formatCurrency(num(selectedStatement.currentPropertyValue))}</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-900">Ingresos por Renta</div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Ingreso bruto del mes</p>
                  <p className="font-semibold text-green-600">{formatCurrency(num(selectedStatement.monthlyGrossIncome ?? selectedStatement.income))}</p>
                </div>
                <div>
                  <p className="text-gray-500">Ingreso neto del mes</p>
                  <p className="font-semibold text-green-600">{formatCurrency(num(selectedStatement.monthlyNetIncome ?? selectedStatement.netIncome))}</p>
                </div>
                <div>
                  <p className="text-gray-500">Ingreso acumulado del año</p>
                  <p className="font-semibold text-green-600">{formatCurrency(num(selectedStatement.accumulatedYearlyIncome))}</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-900">Retornos</div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">ROI mensual (%)</p>
                  <p className="font-semibold text-gray-900">{formatPct(num(selectedStatement.monthlyROI))}</p>
                </div>
                <div>
                  <p className="text-gray-500">ROI anual (%)</p>
                  <p className="font-semibold text-gray-900">{formatPct(num(selectedStatement.annualROI))}</p>
                </div>
                <div>
                  <p className="text-gray-500">Yield anual (%)</p>
                  <p className="font-semibold text-gray-900">{formatPct(num(selectedStatement.annualYield))}</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-900">Plusvalía</div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Valor inicial del activo</p>
                  <p className="font-semibold text-gray-900">{formatCurrency(num(selectedStatement.initialAssetValue))}</p>
                </div>
                <div>
                  <p className="text-gray-500">Valor actual estimado</p>
                  <p className="font-semibold text-gray-900">{formatCurrency(num(selectedStatement.currentEstimatedValue))}</p>
                </div>
                <div>
                  <p className="text-gray-500">Plusvalía anual (%)</p>
                  <p className="font-semibold text-gray-900">{formatPct(num(selectedStatement.annualAppreciation))}</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 font-semibold text-gray-900">Resumen Ejecutivo</div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Flujo neto total recibido</p>
                  <p className="font-semibold text-green-600">{formatCurrency(num(selectedStatement.totalNetFlow ?? selectedStatement.investorShare))}</p>
                </div>
                <div>
                  <p className="text-gray-500">Ganancia total (flujo + plusvalía)</p>
                  <p className="font-semibold text-primary">{formatCurrency(num(selectedStatement.totalGain ?? selectedStatement.investorShare))}</p>
                </div>
                <div>
                  <p className="text-gray-500">Retorno total sobre la inversión (%)</p>
                  <p className="font-semibold text-primary">{formatPct(num(selectedStatement.totalReturnOnInvestment))}</p>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-1">
              <a
                href={selectedStatement.fileUrl ? getPropertyDocumentDownloadUrl(selectedStatement.fileUrl) : '#'}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold ${
                  selectedStatement.fileUrl
                    ? 'bg-fuchsia-500 text-white hover:bg-fuchsia-600'
                    : 'bg-gray-200 text-gray-500 pointer-events-none'
                }`}
              >
                <Download className="w-4 h-4" />
                Descargar PDF
              </a>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={annualDetailOpen}
        onClose={() => {
          setAnnualDetailOpen(false);
          setSelectedAnnual(null);
        }}
        title={
          selectedAnnual
            ? `Resumen anual ${selectedAnnual.year} — ${selectedAnnual.propertyName}`
            : 'Resumen anual'
        }
        size="lg"
      >
        {selectedAnnual?.data ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Ingreso total</p>
                <p className="font-semibold text-green-600">{formatCurrency(selectedAnnual.data.totalIncome)}</p>
              </div>
              <div>
                <p className="text-gray-500">Egreso total</p>
                <p className="font-semibold text-red-500">{formatCurrency(selectedAnnual.data.totalExpenses)}</p>
              </div>
              <div>
                <p className="text-gray-500">Ingreso neto total</p>
                <p className="font-semibold text-gray-900">{formatCurrency(selectedAnnual.data.totalNetIncome)}</p>
              </div>
              <div>
                <p className="text-gray-500">Utilidad total</p>
                <p className="font-semibold text-primary">{formatCurrency(selectedAnnual.data.totalProfit)}</p>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-3 py-2">Mes</th>
                    <th className="text-right px-3 py-2">Ingreso</th>
                    <th className="text-right px-3 py-2">Egreso</th>
                    <th className="text-right px-3 py-2">Neto</th>
                    <th className="text-right px-3 py-2">Utilidad</th>
                    {canDelete && <th className="px-3 py-2" />}
                  </tr>
                </thead>
                <tbody>
                  {(selectedAnnual.data.months || []).map((m) => (
                    <tr key={m.id} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2">{formatMonthYear(m.month, m.year)}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(Number(m.income ?? 0))}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(Number(m.expenses ?? 0))}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(Number(m.netIncome ?? 0))}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(Number(m.investorShare ?? 0))}</td>
                      {canDelete && (
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            disabled={deleteMutation.isPending}
                            onClick={() => {
                              if (!window.confirm(`¿Eliminar el estado de ${formatMonthYear(m.month, m.year)}?`)) return;
                              deleteMutation.mutate(m.id, {
                                onSuccess: () => {
                                  setSelectedAnnual((prev) => prev
                                    ? { ...prev, data: prev.data ? { ...prev.data, months: prev.data.months.filter((x) => x.id !== m.id) } : null }
                                    : null
                                  );
                                },
                              });
                            }}
                            className="p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            title="Eliminar registro"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Sin datos disponibles.</p>
        )}
      </Modal>

    </div>
  );
}
